import { ChangeEvent, FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectOption } from "@idw-types/common";
import { navigateToUtil } from "@utils/navigation-util";
import { MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_URL } from "@constants/routes";
import { InputFormGroup } from "../../../common/input-form-group/input-form-group";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { FormattedAxnAttributeLabel, AxnAttributeType } from "@idw-types/data/reference-data/axn";
import styles from "./attribute-label-details-component.less";
import { updateAttributeLabelReduxRequest } from "@redux-requests/attribute-label-details";
import { useDispatch } from "react-redux";
import { AttributeTypeSelectFormGroup } from "../../../common/select-form-groups";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";

type AttributeLabelDetailsComponentProps = {
    axnAttributeLabel: FormattedAxnAttributeLabel;
    axnAttributeTypes: Array<AxnAttributeType>;
    isDisabled: boolean;
    setIsDisabled: (isDisabled: boolean) => void;
};

type AttributeLabelDetailsComponentState = {
    axnAttributeLabel: FormattedAxnAttributeLabel;
    selectedAttributeTypeOption: SelectOption;
};


export const AttributeLabelDetailsComponent = (props: AttributeLabelDetailsComponentProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { axnAttributeLabel, axnAttributeTypes, isDisabled, setIsDisabled } = props;
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [state, setState] = useState<AttributeLabelDetailsComponentState>({
        axnAttributeLabel: axnAttributeLabel,
        selectedAttributeTypeOption: axnAttributeLabel.attributeType ? {
            label: axnAttributeLabel.attributeType?.name,
            value: axnAttributeLabel.attributeType?.id
        } : null
    });

    const handleAttributeTypeChange = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                axnAttributeLabel: {
                    ...state.axnAttributeLabel,
                    attributeType: axnAttributeTypes.find(at => at.id === newValue.value)
                },
                selectedAttributeTypeOption: newValue
            });
        }
    };

    const handleLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            axnAttributeLabel: {
                ...state.axnAttributeLabel,
                label: event.target.value
            }
        });
    };

    const onCloseClick = () => {
        navigateToUtil(MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_URL, navigate, routerLocation.pathname);
    };

    const onEditClick = () => {
        setIsDisabled(false);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const updateAttributeLabelDetailsResult = await updateAttributeLabelReduxRequest(dispatch)(state.axnAttributeLabel, axnAttributeTypes);
        if (updateAttributeLabelDetailsResult) {
            setState({
                axnAttributeLabel: updateAttributeLabelDetailsResult,
                selectedAttributeTypeOption: updateAttributeLabelDetailsResult.attributeType ? {
                    label: updateAttributeLabelDetailsResult.attributeType.name,
                    value: updateAttributeLabelDetailsResult.attributeType.id
                } : null
            });
        }
        setRequestInProgress(false);
        setIsDisabled(true);
    };

    const onCancelClick = () => {
        setState({
            axnAttributeLabel: axnAttributeLabel,
            selectedAttributeTypeOption: {
                label: axnAttributeLabel.attributeType?.name,
                value: axnAttributeLabel.attributeType?.id
            }
        });
        setIsDisabled(true);
    };

    return (
        <div className={styles.attributeLabelDetailsComponent}>
            <Form onSubmit={handleSubmit}>
                <InputFormGroup
                    controlId="attribute-label-label-input"
                    automationId="attribute-label-label-input"
                    label={resources.common.inputs.attributeLabel.label}
                    ariaLabel={resources.common.inputs.attributeLabel.label}
                    value={state.axnAttributeLabel.label || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleLabelChange}
                    labelWidth={labelWidth}
                />
                <AttributeTypeSelectFormGroup
                    idPrefix="attribute-label"
                    onChange={handleAttributeTypeChange}
                    axnAttributeTypes={axnAttributeTypes}
                    selectedOption={state.selectedAttributeTypeOption}
                    disabled={true}
                    isRequired={true}
                    isSearchable={true}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <div className={styles.buttonDiv}>
                    <Button id="attribute-label-details-close-button" onClick={onCloseClick} variant={"outline-secondary"}>{resources.common.buttons.close.label}</Button>
                    {isDisabled && (
                        <Button id="attribute-label-details-edit-button" onClick={onEditClick}>{resources.common.buttons.edit.label}</Button>
                    )}
                    {!isDisabled && (
                        <>
                            <Button id="attribute-label-details-cancel-button" onClick={onCancelClick} variant={"outline-secondary"}>{resources.common.buttons.cancel.label}</Button>
                            <ButtonWithSpinner
                                id="attribute-label-details-save-button"
                                disabled={requestInProgress}
                                hideSpinner={false}
                                variant="primary"
                                type={ButtonTypes.SUBMIT}
                                label={resources.common.buttons.save.label}
                            />
                        </>
                    )}
                </div>
            </Form>
        </div>
    );
};